import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Sparkles, Pencil } from "lucide-react";
import { api } from "../../lib/api";
import { useJobPoller } from "./AdminLangBar";

const KIND_LABEL = { development: "Developments", blog: "Blog posts", page: "Site pages" };
const PAGE_ROUTES = { home: "/admin/home", header: "/admin/header", footer: "/admin/footer", seo: "/admin/seo", ui: "/admin/ui-strings" };

const editLink = (it) =>
  it.kind === "development"
    ? `/admin/developments/${it.key}/edit`
    : it.kind === "blog"
    ? `/admin/blog/${it.key}/edit`
    : PAGE_ROUTES[it.key] || `/admin/pages/${it.key}`;

function Cell({ s, testId }) {
  const pct = s.total ? Math.round((s.done / s.total) * 100) : 100;
  const cls = pct === 100 ? "bg-[#E8EFE4] text-[#4F6B43]" : pct === 0 ? "bg-[#F6E9E4] text-[#9A5A45]" : "bg-[#F7F0E1] text-[#8A6D2F]";
  return (
    <td data-testid={testId} title={`${s.done}/${s.total} strings`} className={`text-center text-[10px] px-1 py-2 border border-[#F0EBE2] ${cls}`}>
      {pct === 100 ? "✓" : `${pct}%`}
    </td>
  );
}

export default function AdminTranslations() {
  const [data, setData] = useState(null);
  const load = useCallback(async () => {
    const { data: d } = await api.get("/admin/translations/status");
    setData(d);
  }, []);
  const { job, track, running } = useJobPoller(() => {
    toast.success("Translation job finished");
    load();
  });

  useEffect(() => {
    load();
    (async () => {
      const { data: j } = await api.get("/admin/translations/jobs/latest");
      if (j && (j.status === "running" || j.status === "queued")) track(j.id);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  const start = async (body) => {
    try {
      const { data: r } = await api.post("/admin/translations/translate", body);
      track(r.job_id);
      toast.success("Translation started in the background");
    } catch (_e) {
      toast.error("Could not start translation");
    }
  };

  if (!data)
    return (
      <div className="p-10">
        <Loader2 className="w-6 h-6 animate-spin text-[#C5A880]" strokeWidth={1.5} />
      </div>
    );

  const langs = Object.keys(data.languages).filter((c) => c !== "en");
  const groups = Object.keys(KIND_LABEL).map((k) => [k, data.items.filter((i) => i.kind === k)]);

  return (
    <div className="p-10 lg:p-16" data-testid="admin-translations">
      <h1 className="text-4xl lg:text-5xl font-light mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
        Translations
      </h1>
      <p className="text-sm text-[#73706B] mb-8 max-w-3xl">
        English is the master language. Every development, blog post and page is published in {langs.length} more languages.
        Missing or outdated text (English changed since translation) shows in English until translated. Open any item and switch the
        “Editing language” to fine-tune a translation by hand.
      </p>

      <div className="flex flex-wrap items-center gap-4 mb-10">
        <button
          onClick={() => start({})}
          disabled={running}
          data-testid="translate-all-missing-btn"
          className="inline-flex items-center gap-2 bg-[#1A1A1A] text-[#F9F6F0] px-6 py-3.5 text-[10px] tracking-[0.25em] uppercase hover:bg-[#C5A880] transition-colors disabled:opacity-50"
        >
          {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" strokeWidth={1.5} />}
          Translate everything missing
        </button>
        <Link to="/admin/ui-strings" data-testid="edit-ui-strings-link" className="text-[10px] tracking-[0.25em] uppercase border-b border-[#1A1A1A] pb-1 hover:text-[#C5A880] hover:border-[#C5A880]">
          Edit interface text (buttons, labels)
        </Link>
        {job && (
          <div data-testid="translation-job-progress" className="flex items-center gap-3 text-xs text-[#73706B]">
            <div className="w-48 h-1.5 bg-[#E5DFD5]">
              <div className="h-full bg-[#C5A880] transition-[width] duration-500" style={{ width: `${job.total ? (job.done / job.total) * 100 : 0}%` }} />
            </div>
            {job.done}/{job.total} · {job.status}
          </div>
        )}
      </div>

      {groups.map(([kind, items]) => (
        <section key={kind} className="mb-12 overflow-x-auto">
          <h2 className="text-[10px] tracking-[0.3em] uppercase text-[#C5A880] mb-4">— {KIND_LABEL[kind]}</h2>
          <table className="w-full border-collapse bg-[#FFFFFF] text-sm" data-testid={`translations-table-${kind}`}>
            <thead>
              <tr>
                <th className="text-left text-[10px] tracking-[0.2em] uppercase text-[#73706B] px-3 py-2 min-w-[220px]">Item</th>
                {langs.map((l) => (
                  <th key={l} className="text-[10px] uppercase text-[#73706B] px-1 py-2 w-10">{l}</th>
                ))}
                <th className="w-32" />
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.key} data-testid={`translation-row-${it.kind}-${it.key}`}>
                  <td className="px-3 py-2 border border-[#F0EBE2]">
                    <Link to={editLink(it)} className="hover:text-[#C5A880] inline-flex items-center gap-2">
                      <Pencil className="w-3 h-3" strokeWidth={1.5} /> {it.label}
                    </Link>
                  </td>
                  {langs.map((l) => (
                    <Cell key={l} s={it.langs[l]} testId={`translation-cell-${it.key}-${l}`} />
                  ))}
                  <td className="px-2 border border-[#F0EBE2] text-center">
                    <button
                      onClick={() => start({ kind: it.kind, key: it.key })}
                      disabled={running}
                      data-testid={`translate-item-${it.key}`}
                      className="text-[9px] tracking-[0.2em] uppercase text-[#1A1A1A] hover:text-[#C5A880] disabled:opacity-40"
                    >
                      Translate
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ))}
    </div>
  );
}
